import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import loginImage from "../../assets/login.webp";
import registerImage from "../../assets/register.webp";

const DashboardPreview: React.FC = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    { image: loginImage, label: "Login" },
    { image: registerImage, label: "Register" },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <section id="dashboard-preview" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            {t("dashboardPreview.title")}
            <span className="bg-gradient-to-r from-orange-500 to-red-600 bg-clip-text text-transparent">
              {" "}
              {t("dashboardPreview.titleHighlight")}{" "}
            </span>
            {t("dashboardPreview.titleEnd")}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t("dashboardPreview.subtitle")}
          </p>
        </div>

        {/* Dashboard Screenshots Grid */}
        <div className="space-y-16">
          {/* Login/Welcome Screen */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t("dashboardPreview.login.title")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("dashboardPreview.login.description")}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-700">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                  {t("dashboardPreview.login.benefit1")}
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                  {t("dashboardPreview.login.benefit2")}
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                  {t("dashboardPreview.login.benefit3")}
                </li>
              </ul>
            </div>
            <div className="order-1 lg:order-2">
              <div className="shadow-xl">
                <div className="bg-white rounded-xl overflow-hidden relative">
                  {/* Slider Container */}
                  <div className="relative h-64 lg:h-96 overflow-hidden">
                    {/* Slides */}
                    <div
                      className="flex transition-transform duration-500 ease-in-out h-full"
                      style={{
                        transform: `translateX(-${currentSlide * 100}%)`,
                      }}
                    >
                      {slides.map((slide, index) => (
                        <div
                          key={index}
                          className="min-w-full h-full flex items-center justify-center"
                        >
                          <img
                            src={slide.image}
                            alt={slide.label}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Navigation Arrows */}
                    <button
                      onClick={prevSlide}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 z-10"
                      aria-label="Previous slide"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={nextSlide}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-200 hover:scale-110 z-10"
                      aria-label="Next slide"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>

                    {/* Slide Indicators */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10">
                      {slides.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => goToSlide(index)}
                          className={`w-2 h-2 rounded-full transition-all duration-300 ${
                            currentSlide === index
                              ? "bg-white w-8"
                              : "bg-white/50 hover:bg-white/75"
                          }`}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>

                    {/* Slide Label */}
                    <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {slides[currentSlide].label}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Dashboard */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="shadow-xl">
                <div className="bg-white rounded-xl overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      {/* <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm">CR</span>
                        </div>
                        <span className="font-semibold">CHRISTIAN SPACE DASHBOARD</span>
                      </div> */}

                      <img
                        src={require("../../assets/bibles-wallpaper.webp")}
                        alt="Bibles Wallpaper"
                        className="w-full rounded-lg object-cover mr-0 shadow"
                      />
                    </div>
                    <div className="flex space-x-2 mb-5">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                        EN
                      </span>
                      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">
                        ES
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-blue-600 text-white p-4 rounded-lg">
                        <div className="text-xs opacity-80">YouVersion</div>
                      </div>
                      <div className="bg-red-600 text-white p-4 rounded-lg">
                        <div className="text-xs opacity-80">Bible Gateway</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <span className="text-sm">
                          {t("dashboardPreview.integrated.question1")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                        <span className="text-sm">
                          {t("dashboardPreview.integrated.question2")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t("dashboardPreview.integrated.title")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("dashboardPreview.integrated.description")}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-700">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  {t("dashboardPreview.integrated.benefit1")}
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  {t("dashboardPreview.integrated.benefit2")}
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                  {t("dashboardPreview.integrated.benefit3")}
                </li>
              </ul>
            </div>
          </div>

          {/* Growth Profile */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t("dashboardPreview.growth.title")}
              </h3>
              <p className="text-gray-600 mb-6">
                {t("dashboardPreview.growth.description")}
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-700">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                  {t("dashboardPreview.growth.benefit1")}
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                  {t("dashboardPreview.growth.benefit2")}
                </li>
                <li className="flex items-center text-gray-700">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                  {t("dashboardPreview.growth.benefit3")}
                </li>
              </ul>
            </div>
            <div className="order-1 lg:order-2">
              <div className="shadow-xl">
                  <div className="order-1 lg:order-2">
                    <img
                      src={require("../../assets/personal-tracking-banner.webp")}
                      alt="Personal Tracking Banner"
                      className="w-full rounded-2xl shadow-xl object-cover"
                    />
                  </div>
                
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <a
            href="https://christreignsmedia.io/register"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white px-12 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl">
              {t("dashboardPreview.cta.button")}
            </button>
          </a>
          {/* <p className="text-gray-500 mt-4">
            {t("dashboardPreview.cta.subtitle")}
          </p> */}
        </div>
      </div>
    </section>
  );
};

export default DashboardPreview;
